import { useState } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { Button as AntButton, Input, Select, Space, Table, Tag, Typography } from "antd";
import { Button } from "@/shared/components/button";
import { AppModal } from "@/shared/components/dialog";
import type { ColumnsType } from "antd/es/table";
import {
  cancelPhysicianInvitationApi,
  createPhysicianInvitationApi,
  listPhysicianInvitationsApi,
  resendPhysicianInvitationApi,
} from "@/portals/admin/api/adminPhysiciansApi";
import { INVITATION_STATUSES, type InvitationStatus, type PhysicianInvitation } from "@/portals/physician/api/types";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { validateField } from "@/shared/lib/validators";
import { useCachedQuery } from "@/shared/hooks/useCachedQuery";
import { adminQueryKeys } from "@/portals/admin/api/adminQueryKeys";
import { useAdminSocketEvent } from "@/lib/adminSocket";
import { formatDate } from "./formatDate";

export const INVITE_ERROR_MESSAGES: Record<string, string> = {
  EMAIL_ALREADY_REGISTERED: "This email already has a MedSyncIQ account.",
  INVITE_ALREADY_PENDING: "An invitation is already pending for this email.",
};

const ROW_ACTION_ERROR_MESSAGES: Record<string, string> = {
  EMAIL_ALREADY_REGISTERED: "This email already has a MedSyncIQ account.",
  INVITE_NOT_RESENDABLE: "This physician already accepted the invitation.",
  INVITE_NOT_CANCELLABLE: "This invitation can no longer be cancelled.",
};

export const STATUS_LABELS: Record<InvitationStatus, string> = {
  AWAITING_SIGNUP: "Awaiting signup",
  ACCEPTED: "Accepted",
  CANCELLED: "Cancelled",
  EXPIRED: "Expired",
  DECLINED: "Declined",
};

// antd preset colours, not the status presets ("processing" etc.) — this app's
// theme remaps those (processing rendered as a dark grey pill).
const STATUS_COLORS: Record<InvitationStatus, string> = {
  AWAITING_SIGNUP: "blue",
  ACCEPTED: "green",
  CANCELLED: "default",
  EXPIRED: "orange",
  DECLINED: "red",
};

/** Resend: anything not yet accepted — including an invite the physician
 *  declined or an admin cancelled (re-inviting them sends a fresh link). */
const canResend = (status: InvitationStatus) => status !== "ACCEPTED";
/** Cancel: only an invite that is still open. */
const canCancel = (status: InvitationStatus) => status === "AWAITING_SIGNUP" || status === "EXPIRED";

type StatusFilter = InvitationStatus | "ALL";

function InviteForm({ onInvited }: { onInvited: () => void }) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim();
    const invalid = validateField(trimmed, [
      { required: true, message: "Email is required" },
      { type: "email", message: "Enter a valid email address" },
    ]);
    if (invalid) {
      setError(invalid);
      return;
    }
    setError(null);
    setSending(true);
    const { data, error: apiError } = await createPhysicianInvitationApi(trimmed);
    setSending(false);
    if (data) {
      toastSuccess(`Invitation sent to ${data.email}.`);
      setEmail("");
      onInvited();
      return;
    }
    toastError((apiError && INVITE_ERROR_MESSAGES[apiError.code]) ?? apiError?.message ?? "Unable to send the invitation.");
  };

  return (
    <form onSubmit={handleSubmit} noValidate aria-label="Invite a physician">
      <label htmlFor="physician-invite-email" className="mb-2 block text-sm font-semibold text-[#0A2947]">
        Invite a physician
      </label>
      {/* Input and button share one height (h-10) and the row spans the full
          card width, flush with the invitations table below. */}
      <div className="flex w-full items-center gap-3">
        <Input
          id="physician-invite-email"
          aria-label="Physician email"
          placeholder="physician@hospital.org"
          value={email}
          status={error ? "error" : undefined}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError(null);
          }}
          className="!h-10 !flex-1 !rounded-lg !border-[#E5E7EB] !text-sm !text-[#0A2947] placeholder:!text-[#9CA3AF] focus:!border-[#E4002B]/50 focus:!shadow-none"
        />
        <Button
          htmlType="submit"
          loading={sending}
          className="!h-10 !shrink-0 !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-6 !text-sm !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white"
        >
          Send invitation
        </Button>
      </div>
      {error && (
        <p role="alert" className="!mt-1 text-xs text-[#E4002B]">
          {error}
        </p>
      )}
    </form>
  );
}

export function InvitationsTab() {
  const [filter, setFilter] = useState<StatusFilter>("ALL");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState<PhysicianInvitation | null>(null);

  // Cached per status filter: flipping between filters (or tabs) shows each list at once.
  const { data: items = [], loading, cache } = useCachedQuery(
    `${adminQueryKeys.invitations}${filter}`,
    async () => {
      const { data, error } = await listPhysicianInvitationsApi(filter === "ALL" ? undefined : filter);
      return { data: data?.items ?? null, error };
    },
    {
      // A failed refresh behind a list that's already shown stays quiet — after a
      // failed resend/cancel the action's own toast is the one error the admin sees.
      onError: (error, { hadData }) => {
        if (!hadData) toastError(error?.message ?? "Unable to load invitations.");
      },
    },
  );
  // An invite/resend/cancel changes every filter's list — the shown one refetches, the rest reload on next view.
  const load = () => cache.invalidate(adminQueryKeys.invitations);
  // Live: a physician declining from their email arrives as an admin notification.
  useAdminSocketEvent("notification:new", load);

  const handleResend = async (invite: PhysicianInvitation) => {
    setBusyId(invite.id);
    const { data, error } = await resendPhysicianInvitationApi(invite.id);
    setBusyId(null);
    if (data) toastSuccess(`Invitation resent to ${data.email}.`);
    else toastError((error && ROW_ACTION_ERROR_MESSAGES[error.code]) ?? error?.message ?? "Unable to resend the invitation.");
    void load();
  };

  const handleCancelConfirmed = async () => {
    const invite = confirmCancel;
    if (!invite) return;
    setBusyId(invite.id);
    const { data, error } = await cancelPhysicianInvitationApi(invite.id);
    setBusyId(null);
    setConfirmCancel(null);
    if (data) toastSuccess(`Invitation to ${invite.email} cancelled.`);
    else toastError((error && ROW_ACTION_ERROR_MESSAGES[error.code]) ?? error?.message ?? "Unable to cancel the invitation.");
    void load();
  };

  const columns: ColumnsType<PhysicianInvitation> = [
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: InvitationStatus) => <Tag color={STATUS_COLORS[status]}>{STATUS_LABELS[status]}</Tag>,
    },
    { title: "Sent", dataIndex: "createdAt", key: "createdAt", render: formatDate },
    { title: "Expires", dataIndex: "expiresAt", key: "expiresAt", render: formatDate },
    {
      title: "Actions",
      key: "actions",
      render: (_, invite) =>
        canResend(invite.status) ? (
          <Space size="small">
            {/* Fixed width that fits spinner + label, so showing the spinner never
                resizes the button or shifts the row; disabled while it runs. */}
            <AntButton
              size="small"
              disabled={busyId === invite.id}
              aria-busy={busyId === invite.id}
              onClick={() => handleResend(invite)}
              className="!inline-flex !w-[88px] !items-center !justify-center !gap-1.5"
            >
              {busyId === invite.id && <LoadingOutlined aria-hidden />}
              Resend
            </AntButton>
            {canCancel(invite.status) && (
              <AntButton size="small" danger disabled={busyId === invite.id} onClick={() => setConfirmCancel(invite)}>
                Cancel
              </AntButton>
            )}
          </Space>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <InviteForm onInvited={load} />

      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-[#0A2947]">Invitations</p>
        <Select<StatusFilter>
          aria-label="Filter by status"
          value={filter}
          onChange={setFilter}
          className="!w-48"
          options={[
            { value: "ALL", label: "All statuses" },
            ...INVITATION_STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] })),
          ]}
        />
      </div>


      <Table<PhysicianInvitation>
        rowKey="id"
        columns={columns}
        dataSource={items}
        loading={loading}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        locale={{ emptyText: "No invitations found." }}
        scroll={{ x: 720 }}
      />

      <AppModal
        open={confirmCancel !== null}
        onOpenChange={(open) => { if (!open) setConfirmCancel(null); }}
        title={<span className="text-[#0A2947]">Cancel this invitation?</span>}
        description={
          <span className="text-[#4B5563]">
            The invitation link sent to <strong>{confirmCancel?.email}</strong> will stop working immediately.
          </span>
        }
        width={480}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmCancel(null)}
              className="!h-auto !rounded-lg !border-[#E5E7EB] !px-4 !py-2 !text-sm !font-medium !text-[#4B5563] hover:!border-[#E4002B] hover:!text-[#E4002B]"
            >
              Keep invitation
            </Button>
            <Button
              onClick={handleCancelConfirmed}
              loading={busyId !== null && busyId === confirmCancel?.id}
              className="!h-auto !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-4 !py-2 !text-sm !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white"
            >
              Cancel invitation
            </Button>
          </div>
        }
      />
    </div>
  );
}
